/**
 * Maison Select — combobox accesible sobre snippets/maison-select.liquid.
 *
 * Reemplaza únicamente la PRESENTACIÓN del menú de opciones nativo del
 * <select> de "Ordenar por" (imposible de estilizar de forma consistente
 * entre navegadores/mobile — ver comentario en el snippet). El <select>
 * real sigue siendo la fuente de valor: elegir una opción aquí escribe
 * el value en el <select> oculto y dispara 'input' + 'change' nativos,
 * que es lo que assets/facets.js (<facet-filters-form>) ya escucha para
 * lanzar el sorting Ajax existente de Shopify. Cero lógica de sorting
 * nueva, cero parámetros hardcodeados: las opciones vienen siempre del
 * <select> real, renderizado server-side con los sort_options reales.
 *
 * Patrón ARIA: listbox con aria-activedescendant (WAI-ARIA APG "Select
 * Only Combobox" simplificado) — el foco real permanece en el propio
 * listbox mientras está abierto; la opción "activa" se indica vía
 * aria-activedescendant + una clase visual, sin mover el foco DOM por
 * cada opción (evita tabbing extra, evita perder el foco si el listbox
 * se re-renderiza a mitad de navegación).
 *
 * Se re-inicializa solo: al ser un Custom Element, connectedCallback()
 * corre de nuevo cada vez que Dawn reemplaza este bloque vía innerHTML
 * tras un update de facets (mismo mecanismo que <facet-filters-form>/
 * <price-range> en assets/facets.js) — no hace falta un "rebind" manual.
 */
class MaisonSelect extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('.maison-select__trigger');
    this.triggerLabel = this.querySelector('.maison-select__trigger-label');
    this.listbox = this.querySelector('.maison-select__listbox');
    this.nativeSelect = this.querySelector('.maison-select__native');
    if (!this.trigger || !this.listbox || !this.nativeSelect) return;

    this.options = Array.from(this.listbox.querySelectorAll('[role="option"]'));
    this.activeIndex = this.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    if (this.activeIndex < 0) this.activeIndex = 0;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onTriggerKeydown = this.onTriggerKeydown.bind(this);
    this.onListboxKeydown = this.onListboxKeydown.bind(this);
    this.onOptionClick = this.onOptionClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.trigger.addEventListener('keydown', this.onTriggerKeydown);
    this.listbox.addEventListener('keydown', this.onListboxKeydown);
    this.listbox.addEventListener('click', this.onOptionClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  isOpen() {
    return !this.listbox.hidden;
  }

  /* 2026-10-28 — modo proxy (data-proxy-for): este selector solo es la
     cara visible; el valor real vive en el maison-select del drawer de
     filtros. Al abrir, se sincroniza con ese valor (pudo cambiar tras un
     re-render Ajax de facets); al elegir, se le reenvía la opción. */
  proxyTarget() {
    if (!this.dataset.proxyFor) return null;
    var native = document.getElementById(this.dataset.proxyFor);
    var host = native ? native.closest('maison-select') : null;
    return host && typeof host.selectOption === 'function' ? host : null;
  }

  syncFromProxy() {
    var host = this.proxyTarget();
    if (!host) return;
    var value = host.nativeSelect.value;
    this.options.forEach(function (item, index) {
      var selected = item.dataset.value === value;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.classList.toggle('is-selected', selected);
    });
    var index = this.options.findIndex(function (item) {
      return item.dataset.value === value;
    });
    if (index > -1) this.activeIndex = index;
    this.nativeSelect.value = value;
  }

  open() {
    if (this.isOpen()) return;
    this.syncFromProxy();
    this.listbox.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.setActiveOption(this.activeIndex, { scroll: true });
    this.listbox.focus();
    document.addEventListener('click', this.onDocumentClick);
  }

  close(options) {
    var returnFocus = !options || options.returnFocus !== false;
    if (!this.isOpen()) return;
    this.listbox.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.onDocumentClick);
    if (returnFocus) this.trigger.focus();
  }

  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  onTriggerClick(event) {
    event.preventDefault();
    this.toggle();
  }

  onTriggerKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.open();
    }
  }

  onListboxKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.setActiveOption(0, { scroll: true });
        break;
      case 'End':
        event.preventDefault();
        this.setActiveOption(this.options.length - 1, { scroll: true });
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectOption(this.options[this.activeIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'Tab':
        this.close({ returnFocus: false });
        break;
      default:
        break;
    }
  }

  moveActive(delta) {
    var next = Math.min(Math.max(this.activeIndex + delta, 0), this.options.length - 1);
    this.setActiveOption(next, { scroll: true });
  }

  setActiveOption(index, options) {
    var scroll = options && options.scroll;
    var current = this.options[this.activeIndex];
    if (current) current.classList.remove('is-active');

    this.activeIndex = index;
    var option = this.options[index];
    if (!option) return;

    option.classList.add('is-active');
    this.listbox.setAttribute('aria-activedescendant', option.id);
    if (scroll) option.scrollIntoView({ block: 'nearest' });
  }

  onOptionClick(event) {
    var option = event.target.closest('[role="option"]');
    if (!option) return;
    this.selectOption(option);
  }

  selectOption(option) {
    if (!option) return;

    this.options.forEach(function (item) {
      item.setAttribute('aria-selected', 'false');
      item.classList.remove('is-selected');
    });
    option.setAttribute('aria-selected', 'true');
    option.classList.add('is-selected');
    this.activeIndex = this.options.indexOf(option);
    if (this.triggerLabel && !this.hasAttribute('data-static-label')) {
      this.triggerLabel.textContent = option.textContent.trim();
    }

    var value = option.dataset.value;

    var host = this.proxyTarget();
    if (host) {
      this.nativeSelect.value = value;
      var targetOption = host.options.find(function (item) {
        return item.dataset.value === value;
      });
      if (targetOption) host.selectOption(targetOption);
      this.close();
      return;
    }

    if (this.nativeSelect.value !== value) {
      this.nativeSelect.value = value;
      // Programmatic value changes no disparan eventos nativos — hay
      // que despacharlos a mano para que <facet-filters-form> (assets/
      // facets.js, escucha 'input' en el <form>) reaccione exactamente
      // igual que si el usuario hubiera cambiado un <select> real.
      this.nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
      this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.close();
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close({ returnFocus: false });
  }
}

customElements.define('maison-select', MaisonSelect);
